/**
 * wireframeSpherePipeline — 3D sphere with wireframe edges and
 * audio-driven vertex displacement.
 *
 * Raymarched sphere with displacement based on FBM noise + audio.
 * The surface is shaded with rim lighting, and wireframe lines are
 * drawn at latitude/longitude intervals using their SDF distance.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface WireframeSpherePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// Sphere SDF with audio-driven displacement.
float mapSphere(vec3 p) {
  // Slow rotation.
  float a = uTime * 0.2;
  float ca = cos(a), sa = sin(a);
  p.xz = mat2(ca, -sa, sa, ca) * p.xz;
  // Base sphere.
  float baseR = 0.7;
  // Audio: bass expands, mid ripples.
  float r = baseR + 0.1 * sin(uTime * 1.5);
  // Noise displacement.
  float disp = fbm(p.xy * 3.0 + uTime * 0.3) * 0.05;
  r += disp;
  r += bass * 0.05;
  return length(p) - r;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapSphere(p + e.xyy) - mapSphere(p - e.xyy),
    mapSphere(p + e.yxy) - mapSphere(p - e.yxy),
    mapSphere(p + e.yyx) - mapSphere(p - e.yyx)
  ));
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Camera.
  vec3 ro = vec3(0.0, 0.0, 2.0);
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.5 * cw);

  // Raymarch.
  float td = 0.0;
  bool hit = false;
  vec3 hitP = ro;
  for (int i = 0; i < 64; i++) {
    vec3 p2 = ro + rd * td;
    float d = mapSphere(p2);
    if (d < 0.005) { hit = true; hitP = p2; break; }
    td += d * 0.9;
    if (td > 5.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    // Base color: dark with rim lighting.
    vec3 viewDir = normalize(ro - hitP);
    float fresnel = pow(1.0 - max(dot(n, viewDir), 0.0), 3.0);
    // Audio: brightness.
    float brightness = 0.2 + 0.4 * (1.0 - fresnel) + 0.5 * fresnel;
    brightness *= 1.0 + high * 0.3;
    col = vec3(0.10, 0.30, 0.55) * brightness;
    // Audio: shift to magenta with high.
    col = mix(col, vec3(0.95, 0.3, 0.85), high * 0.4);
    // Wireframe: latitude/longitude grid lines.
    float lat = asin(clamp(hitP.y / length(hitP), -1.0, 1.0));
    float lon = atan(hitP.z, hitP.x);
    float latLine = abs(fract(lat * 4.0 / 3.14159265) - 0.5) * 2.0;
    float lonLine = abs(fract(lon * 6.0 / 3.14159265) - 0.5) * 2.0;
    float lineWidth = 0.05;
    float lineIntensity = 1.0 - smoothstep(0.0, lineWidth, min(latLine, lonLine));
    // Audio: brighter lines.
    lineIntensity *= 1.0 + mid * 0.5;
    col += vec3(0.7, 0.95, 1.0) * lineIntensity * 1.5;
  } else {
    // Background: deep gradient.
    float r = length(p);
    col = vec3(0.03, 0.04, 0.08) + vec3(0.05, 0.02, 0.10) * exp(-r * 2.0);
  }

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeWireframeSphereBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeWireframeSphereRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createWireframeSpherePipeline(gl: WebGL2RenderingContext): WireframeSpherePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawWireframeSphere(
  gl: WebGL2RenderingContext,
  pipe: WireframeSpherePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
